import { ContentContainer } from "@/pages/layouts/main-content";
import { Button } from "@/components/ui/button";
import { UserPlus, User, Upload, Download, RefreshCw } from "lucide-react";
import { useTranslation } from "@/providers/language-provider";
import * as TabsLayout from "@/components/blocks/tabs-layout";

import { useSignals } from "@preact/signals-react/runtime";
import { signal } from "@preact/signals-react";
import { FriendList } from "./friends/friend-list";
import { AddFriend } from "./friends/add-friend";
import { SentRequests } from "./friends/sent-requests";
import { ReceivedRequests } from "./friends/received-requests";
import { useChats } from "@/providers/chat-provider";
import {
  updateSentRequests,
  updateReceivedRequests,
} from "@/pages/subs/friends/friend-manager";

export const FRIEND_TABS = {
  FriendList: {
    key: "FriendList",
    icon: <User className="h-4 w-4" />,
  },
  AddFriend: {
    key: "AddFriend",
    icon: <UserPlus className="h-4 w-4" />,
  },
  SentRequests: {
    key: "SentRequests",
    icon: <Upload className="h-4 w-4 rotate-90" />,
  },
  ReceivedRequests: {
    key: "ReceivedRequests",
    icon: <Download className="h-4 w-4 rotate-90" />,
  },
} as const;

export type FriendTab = (typeof FRIEND_TABS)[keyof typeof FRIEND_TABS];

export const friendTab = signal<string>(FRIEND_TABS.FriendList.key);
export const setFrendTab = (tab: FriendTab) => {
  friendTab.value = tab.key;
};

export function MainFriends() {
  useSignals();
  const { t } = useTranslation();
  const { fetchChats } = useChats();

  const handleUpdate = () => {
    fetchChats();

    switch (friendTab.value) {
      case FRIEND_TABS.SentRequests.key:
        updateSentRequests.value();
        break;
      case FRIEND_TABS.ReceivedRequests.key:
        updateReceivedRequests.value();
        break;
    }
  };

  return (
    <ContentContainer>
      <TabsLayout.Root
        value={friendTab.value}
        onValueChange={(value: string) => (friendTab.value = value)}
      >
        <TabsLayout.Top>
          <TabsLayout.Label>
            <span className="ml-2">
              {friendTab.value in FRIEND_TABS
                ? t(
                    FRIEND_TABS[friendTab.value as keyof typeof FRIEND_TABS]
                      .key,
                  )
                : null}
            </span>
          </TabsLayout.Label>
          <TabsLayout.Menu>
            <Button
              className="group"
              variant="ghost"
              size="icon"
              onClick={handleUpdate}
            >
              <RefreshCw className="h-4 w-4 group-hover:animate-spin" />
            </Button>

            <TabsLayout.MenuList>
              {Object.values(FRIEND_TABS).map((item) => {
                return (
                  <TabsLayout.MenuButton key={item.key} value={item.key}>
                    <div className="flex items-center">
                      {item.icon}
                      <span className="hidden md:flex ml-1">{t(item.key)}</span>
                    </div>
                  </TabsLayout.MenuButton>
                );
              })}
            </TabsLayout.MenuList>
          </TabsLayout.Menu>
        </TabsLayout.Top>
        <TabsLayout.Bottom>
          <TabsLayout.Page value={FRIEND_TABS.FriendList.key}>
            {/* <FriendProfile /> */}
            <FriendList />
          </TabsLayout.Page>
          <TabsLayout.Page value={FRIEND_TABS.AddFriend.key}>
            <AddFriend />
          </TabsLayout.Page>
          <TabsLayout.Page value={FRIEND_TABS.SentRequests.key}>
            <SentRequests />
          </TabsLayout.Page>
          <TabsLayout.Page value={FRIEND_TABS.ReceivedRequests.key}>
            <ReceivedRequests />
          </TabsLayout.Page>
        </TabsLayout.Bottom>
      </TabsLayout.Root>
    </ContentContainer>
  );
}
